(()=>{
const magnetCropMath = (() => {
  const FRONT = Object.freeze({width:766,height:768});
  const RESERVE = Object.freeze({width:1024,height:1024,left:129,top:128});
  const clamp = (value,min,max) => Math.max(min,Math.min(max,value));
  function minimumZoom(width,height) {
    return 1.15;
  }
  function rect(width,height,crop={}) {
    if(!Number.isFinite(width)||!Number.isFinite(height)||width<=0||height<=0)throw new Error('Invalid source dimensions');
    const minZoom=minimumZoom(width,height);
    const zoom=clamp(Number.isFinite(crop.zoom)?crop.zoom:minZoom,minZoom,4);
    const scale=Math.max(FRONT.width/width,FRONT.height/height)*zoom;
    const sw=FRONT.width/scale,sh=FRONT.height/scale;
    const mx=sw/(2*width),my=sh/(2*height);
    const cx=clamp(Number.isFinite(crop.cx)?crop.cx:.5,mx,1-mx);
    const cy=clamp(Number.isFinite(crop.cy)?crop.cy:.5,my,1-my);
    return {sx:cx*width-sw/2,sy:cy*height-sh/2,sw,sh,cx,cy,zoom,minZoom};
  }
  function pan(width,height,crop,dx,dy,displayWidth,displayHeight) {
    const r=rect(width,height,crop);
    if(!(displayWidth>0&&displayHeight>0))return r;
    return rect(width,height,{zoom:r.zoom,cx:r.cx-dx*r.sw/(displayWidth*width),cy:r.cy-dy*r.sh/(displayHeight*height)});
  }
  function buttonPan(width,height,crop,dx,dy,displayWidth,displayHeight) {
    let r=rect(width,height,crop);
    if(!(displayWidth>0&&displayHeight>0))return r;
    const needsRoom=(dx!==0&&width-r.sw<1e-7)||(dy!==0&&height-r.sh<1e-7);
    if(needsRoom){
      const extra=2*Math.max(Math.abs(dx)/displayWidth,Math.abs(dy)/displayHeight);
      r=rect(width,height,{...r,zoom:Math.min(4,Math.ceil(r.zoom*(1+extra)*20-1e-9)/20)});
    }
    return pan(width,height,r,dx,dy,displayWidth,displayHeight);
  }
  return Object.freeze({FRONT,RESERVE,minimumZoom,rect,pan,buttonPan});
})();


const magnetPrintUpload = (() => {
  const FRONT = Object.freeze({width:766,height:768});
  const TILE = Object.freeze({width:1024,height:1024,left:129,top:128});
  const MAX_PHOTO_BYTES = 1280 * 1024;
  const MAX_BATCH_BYTES = 16 * 1024 * 1024;

  function plan(width,height,crop) {
    const r = magnetCropMath.rect(width,height,crop);
    const ax=r.sw/FRONT.width,ay=r.sh/FRONT.height;
    return {sx:r.sx-TILE.left*ax,sy:r.sy-TILE.top*ay,sw:TILE.width*ax,sh:TILE.height*ay};
  }
  function segments(start,span,sourceLength,targetLength) {
    const first=Math.max(0,start),last=Math.min(sourceLength,start+span);
    const left=(first-start)*targetLength/span,right=(last-start)*targetLength/span;
    const parts=[];
    if(left>0)parts.push({s:0,n:1,d:0,l:left});
    parts.push({s:first,n:last-first,d:left,l:right-left});
    if(right<targetLength)parts.push({s:sourceLength-1,n:1,d:right,l:targetLength-right});
    return parts;
  }
  function paint(context,image,width,height,crop) {
    const p=plan(width,height,crop);
    context.fillStyle='#fff';context.fillRect(0,0,TILE.width,TILE.height);
    context.imageSmoothingEnabled=true;context.imageSmoothingQuality='high';
    const xs=segments(p.sx,p.sw,width,TILE.width),ys=segments(p.sy,p.sh,height,TILE.height);
    for(const x of xs)for(const y of ys){
      if(x.n>0&&y.n>0&&x.l>0&&y.l>0)context.drawImage(image,x.s,y.s,x.n,y.n,x.d,y.d,x.l,y.l);
    }
  }
  async function previewFile(file,index) {
    const r=magnetCropMath.rect(file.width,file.height,file.crop);
    const canvas=document.createElement('canvas');canvas.width=128;canvas.height=128;
    const context=canvas.getContext('2d');context.fillStyle='#fff';context.fillRect(0,0,128,128);
    context.imageSmoothingEnabled=true;context.imageSmoothingQuality='high';
    const rx=file.image.naturalWidth/file.width,ry=file.image.naturalHeight/file.height;
    context.drawImage(file.image,r.sx*rx,r.sy*ry,r.sw*rx,r.sh*ry,0,0,128,128);
    const output=await blob(canvas,'image/jpeg',.8);
    if(output.size>20*1024)throw new Error('Preview exceeds upload budget');
    return new File([output],`atelier-photo-${index+1}-preview.jpg`,{type:'image/jpeg'});
  }
  const blob=(canvas,type,quality)=>new Promise((resolve,reject)=>canvas.toBlob(value=>value?resolve(value):reject(new Error('Image preparation failed')),type,quality));
  function record(file) {
    const r=magnetCropMath.rect(file.width,file.height,file.crop);
    return {v:'rigid-front-v1',w:file.width,h:file.height,z:Number(r.zoom.toFixed(8)),cx:Number(r.cx.toFixed(8)),cy:Number(r.cy.toFixed(8))};
  }
  async function prepare(file,index) {
    const key=JSON.stringify(record(file));
    if(file.printUpload?.key===key)return file.printUpload;
    if(file.original.size<=MAX_PHOTO_BYTES){
      const result={key,file:file.original,crop:record(file),preview:await previewFile(file,index),prepared:false};
      file.printUpload=result;return result;
    }
    let decoded,sourceURL;
    try{
      if(typeof createImageBitmap==='function')decoded=await createImageBitmap(file.original,{imageOrientation:'from-image'});
      else{
        sourceURL=URL.createObjectURL(file.original);
        decoded=await new Promise((resolve,reject)=>{const image=new Image();image.onload=()=>resolve(image);image.onerror=reject;image.src=sourceURL;});
      }
      const width=decoded.width||decoded.naturalWidth,height=decoded.height||decoded.naturalHeight;
      if(width!==file.width||height!==file.height)throw new Error('Source dimensions changed');
      const canvas=document.createElement('canvas');canvas.width=TILE.width;canvas.height=TILE.height;
      paint(canvas.getContext('2d'),decoded,width,height,file.crop);
      let output=await blob(canvas,'image/png'),extension='png';
      if(output.size>MAX_PHOTO_BYTES){
        extension='jpg';
        for(const quality of [.98,.97,.96,.95,.94]){
          output=await blob(canvas,'image/jpeg',quality);
          if(output.size<=MAX_PHOTO_BYTES)break;
        }
      }
      if(output.size>MAX_PHOTO_BYTES)throw new Error('Prepared photo exceeds upload budget');
      const result={key,file:new File([output],`atelier-photo-${index+1}-print.${extension}`,{type:output.type}),
        crop:{v:'rigid-front-v1',w:TILE.width,h:TILE.height,z:4/3,cx:.5,cy:.5},preview:await previewFile(file,index),prepared:true};
      file.printUpload=result;return result;
    }finally{decoded?.close?.();if(sourceURL)URL.revokeObjectURL(sourceURL);}
  }
  return Object.freeze({FRONT,TILE,MAX_PHOTO_BYTES,MAX_BATCH_BYTES,plan,segments,paint,prepare,record});
})();

for(const root of document.querySelectorAll('[data-atelier-rigid-editor]')){
  if(root.dataset.ready)continue;
  root.dataset.ready='true';
  const config=JSON.parse(root.querySelector('[data-mag-config]').textContent);
  const form=root.querySelector('form'),shop=root.querySelector('.mag-shop');
  shop.append(root.querySelector('template').content.cloneNode(true));
  root.querySelector('.mag-loading').hidden=true;
  const select=selector=>shop.querySelector(selector);
  const heading=root.querySelector('.mag-product-heading'),cover=root.querySelector('.mag-cover');
  if(heading)select('.mag-main').prepend(heading);if(cover)select('.mag-upload-panel').after(cover);
  const initial=config.variants.find(v=>v.id===config.initialVariant)||config.variants.find(v=>v.available)||config.variants[0];
  const state={pack:Number(initial.option1),finish:initial.option2,files:[],active:null,loading:false,submitting:false,message:'',errors:[],uploadId:null,uncertain:false};
  const t=(key,values={})=>Object.entries(values).reduce((text,[name,value])=>text.replaceAll('__'+name+'__',String(value)),config.strings[key]||key);
  const money=n=>new Intl.NumberFormat(config.locale,{style:'currency',currency:config.currency}).format(Number(n)/100);
  const visibleFiles=()=>state.files.slice(0,state.pack);
  const activeFile=()=>state.files.find(f=>f.id===state.active);
  function saveActivePhoto(){
    const file=activeFile();if(!file||state.loading||state.submitting)return;
    if(file.image?.naturalWidth>0&&file.image?.naturalHeight>0&&file.width>0&&file.height>0){fit(file);file.confirmed=true;}
  }
  const readyPhoto=file=>file.confirmed||(file.id===state.active&&file.image?.naturalWidth>0&&file.image?.naturalHeight>0);
  const variant=()=>config.variants.find(v=>Number(v.option1)===state.pack&&v.option2===state.finish);
  const panDelta={left:[-12,0],up:[0,-12],down:[0,12],right:[12,0]};
  let serial=0;
  function icon(name){const span=document.createElement('span');span.setAttribute('aria-hidden','true');span.textContent=({plus:'+',x:'×'})[name]||'';return span;}
  function fit(file){return magnetCropMath.rect(file.width,file.height,file.crop);}
  function paint(canvas,file){
    const r=fit(file),context=canvas.getContext('2d');
    const rx=file.image.naturalWidth/file.width,ry=file.image.naturalHeight/file.height;
    context.clearRect(0,0,canvas.width,canvas.height);context.imageSmoothingEnabled=true;context.imageSmoothingQuality='high';
    context.drawImage(file.image,r.sx*rx,r.sy*ry,r.sw*rx,r.sh*ry,0,0,canvas.width,canvas.height);
    canvas.dataset.crop=JSON.stringify({v:'rigid-front-v1',w:file.width,h:file.height,z:Number(r.zoom.toFixed(8)),cx:Number(r.cx.toFixed(8)),cy:Number(r.cy.toFixed(8))});
  }
  function mutateCrop(next,notice=''){
    const file=activeFile();if(!file||state.submitting)return;
    if(Math.abs(next.cx-file.crop.cx)<1e-10&&Math.abs(next.cy-file.crop.cy)<1e-10&&next.zoom===file.crop.zoom)return;
    file.crop={cx:next.cx,cy:next.cy,zoom:next.zoom};state.uploadId=null;state.uncertain=false;file.confirmed=false;file.panNotice=notice;
    renderEditor();renderSummary();
  }
  function zoomTo(value){const file=activeFile();if(file)mutateCrop(magnetCropMath.rect(file.width,file.height,{...file.crop,zoom:value}));}
  function panBy(dx,dy,fromButton=false){
    const file=activeFile(),box=select('.mag-crop-surface').getBoundingClientRect();
    if(!file||!box.width||!box.height)return;
    const before=fit(file),method=fromButton?magnetCropMath.buttonPan:magnetCropMath.pan;
    const next=method(file.width,file.height,file.crop,dx,dy,box.width,box.height);
    mutateCrop(next,fromButton&&next.zoom>before.zoom?t('pan_zoom',{count:Math.round(next.zoom*100)}):'');
  }
  function imageFromSource(src){return new Promise((resolve,reject)=>{const image=new Image();image.onload=()=>resolve(image);image.onerror=()=>reject(new Error(t('decode_error')));image.src=src;});}
  async function rejectAnimation(original){
    const header=new Uint8Array(await original.slice(0,128).arrayBuffer());
    const text=(start,end)=>String.fromCharCode(...header.slice(start,end));
    let animated=text(0,4)==='RIFF'&&text(8,12)==='WEBP'&&text(12,16)==='VP8X'&&Boolean(header[20]&2);
    if(text(4,8)==='ftyp')for(let offset=8;offset+4<=Math.min(header.length,new DataView(header.buffer).getUint32(0));offset+=4)if(text(offset,offset+4)==='avis')animated=true;
    if(header[0]===137&&text(1,4)==='PNG'){
      let offset=8;
      for(let chunks=0;chunks<256&&offset+8<=original.size;chunks++){
        const chunk=new Uint8Array(await original.slice(offset,offset+8).arrayBuffer());
        const kind=String.fromCharCode(...chunk.slice(4));
        if(kind==='acTL'){animated=true;break;}
        if(kind==='IDAT'||kind==='IEND')break;
        offset+=new DataView(chunk.buffer).getUint32(0)+12;
      }
    }
    if(animated)throw new Error(t('animated_error'));
  }
  async function preview(original){
    const type=original.type.toLowerCase(),extension=original.name.split('.').pop().toLowerCase();
    if(!['image/jpeg','image/png','image/webp','image/avif'].includes(type)&&!(type===''&&['jpg','jpeg','png','webp','avif'].includes(extension)))throw new Error(t('format_error'));
    if(original.size>30*1024*1024)throw new Error(t('size_error'));
    await rejectAnimation(original);
    let decoded,sourceURL;
    try{
      if(typeof createImageBitmap==='function')decoded=await createImageBitmap(original,{imageOrientation:'from-image'});
      else{sourceURL=URL.createObjectURL(original);decoded=await imageFromSource(sourceURL);}
      const width=decoded.width||decoded.naturalWidth,height=decoded.height||decoded.naturalHeight;
      if(width*height>40000000)throw new Error(t('dimensions_error'));
      const factor=Math.min(1,1600/Math.max(width,height));
      const canvas=document.createElement('canvas');canvas.width=Math.max(1,Math.round(width*factor));canvas.height=Math.max(1,Math.round(height*factor));
      const context=canvas.getContext('2d');context.fillStyle='#fff';context.fillRect(0,0,canvas.width,canvas.height);context.drawImage(decoded,0,0,canvas.width,canvas.height);
      const blob=await new Promise(resolve=>canvas.toBlob(resolve,'image/webp',.7));
      if(!blob)throw new Error(t('decode_error'));
      const src=URL.createObjectURL(blob);
      try{return {src,image:await imageFromSource(src),width,height};}catch(error){URL.revokeObjectURL(src);throw error;}
    }catch(error){throw new Error(error.message===t('dimensions_error')?error.message:t('decode_error'));}
    finally{decoded?.close?.();if(sourceURL)URL.revokeObjectURL(sourceURL);}
  }
  async function addFiles(selected,replaceId=null){
    if(state.loading||!selected.length)return;
    saveActivePhoto();state.loading=true;state.uploadId=null;state.uncertain=false;state.errors=[];state.message='';renderSummary();
    const capacity=replaceId?1:Math.max(0,state.pack-visibleFiles().length);
    if(selected.length>capacity)state.errors.push(t('capacity_error',{count:state.pack}));
    let first=null,accepted=0;
    for(const original of selected){
      if(accepted>=capacity)break;
      try{
        const data=await preview(original);
        const entry={...data,id:replaceId||'photo-'+(++serial),original,crop:magnetCropMath.rect(data.width,data.height),confirmed:false};
        const at=replaceId?state.files.findIndex(f=>f.id===replaceId):-1;
        if(at>=0){URL.revokeObjectURL(state.files[at].src);state.files[at]=entry;}
        else state.files.splice(Math.min(state.files.length,state.pack),0,entry);
        first??=entry.id;accepted++;
      }catch(error){state.errors.push(error.message||t('decode_error'));}
    }
    state.loading=false;state.active=first||state.active;
    if(accepted)state.message=t(accepted===1?'added_one':'added_many',{count:accepted});
    render();
  }
  function renderGrid(){
    const grid=select('.mag-photo-grid');grid.replaceChildren();
    const files=visibleFiles();
    for(let index=0;index<state.pack;index++){
      const file=files[index],tile=document.createElement('div');tile.className='mag-photo-tile';
      const button=document.createElement(file?'button':'label');if(file)button.type='button';button.className='mag-tile-button';
      if(file){
        button.dataset.photoId=file.id;button.setAttribute('aria-label',t('frame_photo',{count:index+1}));button.setAttribute('aria-pressed',String(state.active===file.id));
        const canvas=document.createElement('canvas');canvas.width=192;canvas.height=193;canvas.setAttribute('aria-hidden','true');paint(canvas,file);button.append(canvas);
        button.addEventListener('click',()=>{if(state.active!==file.id)saveActivePhoto();state.active=file.id;state.message='';render();select('.mag-back').focus();});
        const remove=document.createElement('button');remove.type='button';remove.className='mag-remove';remove.setAttribute('aria-label',t('remove_photo',{count:index+1}));remove.append(icon('x'));
        remove.addEventListener('click',()=>{state.uploadId=null;state.uncertain=false;const at=state.files.findIndex(f=>f.id===file.id);URL.revokeObjectURL(state.files.splice(at,1)[0].src);if(state.active===file.id)state.active=null;state.message=t('removed');render();});
        tile.append(button,remove);
        const label=document.createElement('div');label.className='mag-tile-label';
        const name=document.createElement('span');name.textContent=t('photo',{count:index+1});
        const status=document.createElement('span');status.className='mag-tile-state'+(readyPhoto(file)?' is-confirmed':'');status.textContent=t(readyPhoto(file)?'confirmed':'to_frame');label.append(name,status);tile.append(label);
      }else{
        button.classList.add('mag-empty');button.setAttribute('aria-label',t('add_photo',{count:index+1}));button.append(icon('plus'));
        const label=document.createElement('span');label.textContent=t('photo',{count:index+1});button.append(label);
        const picker=document.createElement('input');picker.type='file';picker.multiple=true;picker.accept='image/jpeg,image/png,image/webp,image/avif';picker.className='mag-tile-picker mag-native-picker';picker.setAttribute('aria-label',t('add_photo',{count:index+1}));
        picker.addEventListener('change',()=>{const files=[...picker.files];picker.value='';if(files.length)window.AtelierPrivatePhotos?.warm();addFiles(files);});button.append(picker);tile.append(button);
      }
      grid.append(tile);
    }
  }
  function renderEditor(){
    const file=activeFile();shop.classList.toggle('has-editor',Boolean(file));select('.mag-editor').hidden=!file;
    if(!file)return;
    select('.mag-confirm').textContent=file.confirmed?'✓ '+t('confirmed'):t('confirm');
    const index=visibleFiles().findIndex(f=>f.id===file.id),r=fit(file);
    select('.mag-photo-number').textContent=t('photo_number',{count:index+1,total:state.pack});paint(select('.mag-canvas'),file);
    select('.mag-zoom').min=String(Math.round(r.minZoom*100));select('.mag-zoom').value=String(Math.round(r.zoom*100));select('.mag-zoom-value').textContent=Math.round(r.zoom*100)+' %';
    select('.mag-zoom-out').disabled=r.zoom<=r.minZoom+1e-9;select('.mag-zoom-in').disabled=r.zoom>=4;
    const box=select('.mag-crop-surface').getBoundingClientRect();let atEdge=false;
    for(const button of shop.querySelectorAll('[data-pan]')){
      const [dx,dy]=panDelta[button.dataset.pan],next=magnetCropMath.buttonPan(file.width,file.height,r,dx,dy,box.width||340,box.height||340*768/766);
      button.disabled=Math.abs(next.cx-r.cx)<1e-9&&Math.abs(next.cy-r.cy)<1e-9&&next.zoom===r.zoom;atEdge||=button.disabled;
    }
    select('.mag-pan-help').textContent=file.panNotice||(atEdge?t('pan_edge'):r.zoom===1?t('pan_initial'):'');
    select('.mag-quality').hidden=!(r.sw<766||r.sh<768);select('.mag-quality').textContent=t('low_quality');
    const button=select(`.mag-tile-button[data-photo-id="${file.id}"]`);
    if(button){paint(button.querySelector('canvas'),file);button.setAttribute('aria-pressed','true');const status=button.closest('.mag-photo-tile').querySelector('.mag-tile-state');status.textContent=t(readyPhoto(file)?'confirmed':'to_frame');status.classList.toggle('is-confirmed',readyPhoto(file));}
  }
  function renderSummary(){
    const files=visibleFiles(),confirmed=files.filter(readyPhoto).length,missing=state.pack-files.length,current=variant();
    form.elements.id.value=current?.id||'';
    select('.mag-count').textContent=`${files.length} / ${state.pack}`;
    for(const input of shop.querySelectorAll('input[name="mag-pack"]')){input.checked=Number(input.value)===state.pack;input.disabled=state.loading||state.submitting;}
    for(const input of shop.querySelectorAll('input[name="mag-finish"]')){input.checked=input.value===state.finish;input.disabled=state.loading||state.submitting;}
    for(const label of shop.querySelectorAll('[data-pack-price]')){const v=config.variants.find(v=>Number(v.option1)===Number(label.dataset.packPrice)&&v.option2===state.finish);label.textContent=v?money(v.price):'';}
    const prices=config.variants.filter(v=>Number(v.option1)===state.pack).map(v=>Number(v.price));
    const surcharge=Math.max(...prices)-Math.min(...prices);
    select('.mag-film-help').textContent=surcharge?t('film_surcharge',{price:money(surcharge)}):'';
    for(const picker of shop.querySelectorAll('.mag-tile-picker'))picker.disabled=state.loading||state.submitting;
    for(const control of shop.querySelectorAll('.mag-remove,.mag-tile-button,.mag-confirm,.mag-back,.mag-reset,.mag-zoom,textarea'))control.disabled=state.loading||state.submitting;
    if(state.submitting){for(const control of shop.querySelectorAll('.mag-zoom-in,.mag-zoom-out,[data-pan]'))control.disabled=true;}else if(activeFile())renderEditor();
    for(const error of shop.querySelectorAll('.mag-upload-error')){
      error.hidden=!state.errors.length||(error.classList.contains('mag-editor-error')?!activeFile():Boolean(activeFile()));
      error.textContent=[...new Set(state.errors)].join(' ');
    }
    select('.mag-saved-status').textContent=(state.loading?t('opening'):state.message||t('confirmed_count',{count:confirmed,total:state.pack}))+(state.files.length>state.pack?' '+t('retained',{count:state.pack}):'');
    select('.mag-order-summary').textContent=t('summary',{count:state.pack});select('.mag-total').textContent=current?money(current.price):'';if(select('.mag-top-price'))select('.mag-top-price').textContent=current?money(current.price):'';
    select('.mag-cart').disabled=missing>0||!files.every(readyPhoto)||state.loading||state.submitting||!current?.available;
    select('.mag-cart').textContent=state.submitting?t('uploading'):t('cart_add');
    select('.mag-order-help').textContent=!current?.available?t('unavailable'):missing>0?t(missing===1?'missing_one':'missing_many',{count:missing}):confirmed<state.pack?t(state.pack-confirmed===1?'unconfirmed_one':'unconfirmed_many',{count:state.pack-confirmed}):t('ready');
    const nativePrice=document.querySelector('[id^="price-"] .price');
    if(nativePrice&&current){for(const price of nativePrice.querySelectorAll('.price__regular .price-item--regular,.price__sale .price-item--sale'))price.textContent=money(current.price);nativePrice.classList.toggle('price--sold-out',!current.available);}
  }
  function render(){renderGrid();renderEditor();renderSummary();}
  for(const input of shop.querySelectorAll('input[name="mag-pack"]'))input.addEventListener('change',()=>{saveActivePhoto();state.uploadId=null;state.uncertain=false;state.pack=Number(input.value);if(!visibleFiles().some(f=>f.id===state.active))state.active=null;state.errors=[];state.message='';render();});
  for(const input of shop.querySelectorAll('input[name="mag-finish"]'))input.addEventListener('change',()=>{state.uploadId=null;state.uncertain=false;state.finish=input.value;renderSummary();});
  select('.mag-back').addEventListener('click',()=>{saveActivePhoto();const id=state.active;state.active=null;render();const target=select(`.mag-tile-button[data-photo-id="${id}"]`)||select('.mag-tile-picker');target?.focus();});
  select('.mag-reset').addEventListener('click',()=>{const file=activeFile();if(file)mutateCrop(magnetCropMath.rect(file.width,file.height,{zoom:1,cx:.5,cy:.5}));});
  select('.mag-zoom').addEventListener('input',event=>zoomTo(Number(event.target.value)/100));
  select('.mag-zoom-out').addEventListener('click',()=>zoomTo((activeFile()?.crop.zoom||1)-.1));
  select('.mag-zoom-in').addEventListener('click',()=>zoomTo((activeFile()?.crop.zoom||1)+.1));
  select('.mag-confirm').addEventListener('click',()=>{const file=activeFile();if(!file)return;const id=file.id;file.confirmed=true;state.message=t('saved',{count:visibleFiles().findIndex(f=>f.id===id)+1});state.active=null;render();select(`.mag-tile-button[data-photo-id="${id}"]`).focus();});
  for(const button of shop.querySelectorAll('[data-pan]'))button.addEventListener('click',()=>panBy(...panDelta[button.dataset.pan],true));
  const surface=select('.mag-crop-surface'),pointers=new Map();let lastPinch=null;
  surface.addEventListener('pointerdown',event=>{if(!activeFile())return;surface.setPointerCapture(event.pointerId);pointers.set(event.pointerId,{x:event.clientX,y:event.clientY});lastPinch=null;event.preventDefault();});
  surface.addEventListener('pointermove',event=>{if(!pointers.has(event.pointerId)||!activeFile())return;const previous=pointers.get(event.pointerId);pointers.set(event.pointerId,{x:event.clientX,y:event.clientY});if(pointers.size===1)panBy(event.clientX-previous.x,event.clientY-previous.y);else{const [a,b]=[...pointers.values()],distance=Math.hypot(a.x-b.x,a.y-b.y);if(lastPinch&&distance>0)zoomTo(activeFile().crop.zoom*distance/lastPinch);lastPinch=distance;}event.preventDefault();});
  for(const name of ['pointerup','pointercancel','lostpointercapture'])surface.addEventListener(name,event=>{pointers.delete(event.pointerId);lastPinch=null;});
  function route(kind){
    const url=new URL(kind==='add'?form.action:config.cartUrl,window.location.href);
    url.pathname=url.pathname.replace(/\.js$/,'')+'.js';return url;
  }
  async function currentUploadIsInCart(){
    const response=await fetch(route('cart'),{credentials:'same-origin',headers:{Accept:'application/json'}});
    if(!response.ok)throw new Error('Cart verification failed');
    const cart=await response.json();
    return cart.items?.some(item=>item.properties?._MagnetUploadId===state.uploadId);
  }
  shop.querySelector('textarea')?.addEventListener('input',()=>{state.uploadId=null;state.uncertain=false;});
  form.addEventListener('submit',async event=>{
    event.preventDefault();
    if(state.submitting)return;
    saveActivePhoto();
    if(state.loading||!variant()?.available||visibleFiles().length!==state.pack||visibleFiles().some(f=>!f.confirmed)){renderSummary();return;}
    state.submitting=true;state.errors=[];root.dataset.uploadState='preparing';renderSummary();
    try{
      if(state.uncertain&&state.uploadId&&await currentUploadIsInCart()){window.location.assign(config.cartUrl);return;}
      state.uploadId ||= window.AtelierPrivatePhotos.batchId();
      const prepared=[];
      for(const [index,file] of visibleFiles().entries())prepared.push(await magnetPrintUpload.prepare(file,index));
      const total=prepared.reduce((sum,item)=>sum+item.file.size+item.preview.size,0);root.dataset.uploadBytes=String(total);
      if(total>magnetPrintUpload.MAX_BATCH_BYTES)throw new Error('Combined upload exceeds budget');
      form.querySelectorAll('[data-magnet-property]').forEach(input=>input.remove());
      function property(name,value){const input=document.createElement('input');input.type='hidden';input.name=`properties[${name}]`;input.value=value;input.dataset.magnetProperty='true';form.append(input);}
      property('_Production Product','rigid-photo-magnet-v1');property('_MagnetPack',String(state.pack));property('_MagnetUploadId',state.uploadId);
      const privateProperties=await window.AtelierPrivatePhotos.upload(prepared,state.uploadId);
      for(const [key,value] of Object.entries(privateProperties))property(key,value);
      const payload=new FormData(form);
      // Locked editor controls are excluded from FormData; notes must still travel.
      const notes=shop.querySelector('textarea');if(notes)payload.set(notes.name,notes.value);
      state.uncertain=true;root.dataset.uploadState='uploading';
      const response=await fetch(route('add'),{method:'POST',body:payload,credentials:'same-origin',headers:{Accept:'application/json'}});
      root.dataset.uploadStatus=String(response.status);
      if(!response.ok){if(response.status<500)state.uncertain=false;throw new Error('Cart upload rejected');}
      await response.json();
      window.location.assign(config.cartUrl);
    }catch{
      root.dataset.uploadState='error';state.errors=[t('upload_error')];state.submitting=false;renderSummary();
    }
  });
  window.addEventListener('pageshow',()=>{state.submitting=false;renderSummary();});
  render();
}

})();

